import { UIMsg, Conversation } from "../types";
import { useAppSettings } from "../../../components/providers/AppSettingsProvider";
import { Pencil, Check, X } from "lucide-react";
import { useState, useRef, useEffect } from "react";

interface ConversationListProps {
    conversations: Conversation[];
    greetingMessage: { name: string; greeting: string };
    onContinue: (id: string) => void;
    /** If provided, show pencil icon so user can edit their name */
    onSaveName?: (newName: string) => Promise<void>;
}

export function ConversationList({
    conversations,
    greetingMessage,
    onContinue,
    onSaveName,
}: ConversationListProps) {
    const { theme, t } = useAppSettings();
    const isDark = theme === 'dark';

    // ─── Inline name edit state ───────────────────────────────────────────────
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState(greetingMessage.name);
    const [isSaving, setIsSaving] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isEditing) {
            setEditValue(greetingMessage.name);
            setTimeout(() => inputRef.current?.focus(), 50);
        }
    }, [isEditing, greetingMessage.name]);

    const handleSave = async () => {
        const trimmed = editValue.trim();
        if (!trimmed || !onSaveName) { setIsEditing(false); return; }
        try {
            setIsSaving(true);
            await onSaveName(trimmed);
        } finally {
            setIsSaving(false);
            setIsEditing(false);
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') handleSave();
        if (e.key === 'Escape') setIsEditing(false);
    };
    // ─────────────────────────────────────────────────────────────────────────

    const formatCardDate = (timestamp: number): string => {
        const now = Date.now();
        const diff = now - timestamp;
        const minutes = Math.floor(diff / 60000);
        const hours = Math.floor(diff / 3600000);
        const days = Math.floor(diff / 86400000);

        const date = new Date(timestamp);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const convDate = new Date(timestamp);
        convDate.setHours(0, 0, 0, 0);

        const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

        let relativeTime = '';
        if (minutes < 60) {
            relativeTime = t('whisper.minutesAgo', { minutes });
        } else if (hours < 24 && convDate.getTime() === today.getTime()) {
            relativeTime = t('whisper.today');
        } else if (days === 1) {
            relativeTime = t('whisper.yesterday');
        } else {
            return dateStr;
        }
        return `${relativeTime}, ${dateStr}`;
    };

    const getConversationSnippet = (messages: UIMsg[]): string => {
        if (messages.length === 0) return '';
        const lastMsg = messages[messages.length - 1];
        if (!lastMsg || !lastMsg.text) return '';
        const text = lastMsg.text.trim();
        if (text.length > 60) {
            return text.slice(0, 60) + '...';
        }
        return text;
    };

    const today = new Date().toDateString();
    const todayConversations = conversations.filter(conv => {
        const convDate = new Date(conv.updatedAt || conv.createdAt).toDateString();
        return convDate === today;
    });

    return (
        <>
            {/* Greeting */}
            <div className="absolute left-0 right-0 z-40 flex flex-col items-center gap-4" style={{ top: '18vh' }}>
                <div className="text-center px-4">
                    {isEditing ? (
                        /* ── Edit mode ─────────────────────────────────── */
                        <div className="flex items-center justify-center gap-2">
                            <span className={`text-lg font-medium ${isDark ? 'text-white' : 'text-gray-800'}`}>Hi</span>
                            <input
                                ref={inputRef}
                                value={editValue}
                                onChange={e => setEditValue(e.target.value)}
                                onKeyDown={handleKeyDown}
                                placeholder={t('whisper.editName.placeholder')}
                                maxLength={30}
                                className={`text-lg font-medium bg-transparent border-b-2 outline-none text-center w-36
                                    ${isDark
                                        ? 'text-white border-white/60 placeholder:text-white/30'
                                        : 'text-gray-800 border-gray-500/60 placeholder:text-gray-400'
                                    }`}
                            />
                            {/* Confirm */}
                            <button
                                onClick={handleSave}
                                disabled={isSaving}
                                className={`p-1 rounded-full transition-colors ${isDark ? 'text-green-400 hover:text-green-300' : 'text-green-600 hover:text-green-500'}`}
                                aria-label={t('whisper.editName.save')}
                            >
                                <Check size={16} strokeWidth={2.5} />
                            </button>
                            {/* Cancel */}
                            <button
                                onClick={() => setIsEditing(false)}
                                className={`p-1 rounded-full transition-colors ${isDark ? 'text-white/50 hover:text-white/70' : 'text-gray-400 hover:text-gray-600'}`}
                                aria-label="Cancel"
                            >
                                <X size={16} strokeWidth={2.5} />
                            </button>
                        </div>
                    ) : (
                        /* ── Display mode ───────────────────────────────── */
                        <div className="flex items-center justify-center gap-1.5">
                            <p className={`text-lg font-medium leading-relaxed ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                Hi {greetingMessage.name}
                            </p>
                            {onSaveName && (
                                <button
                                    onClick={() => setIsEditing(true)}
                                    className={`mb-0.5 transition-opacity opacity-50 hover:opacity-100 ${isDark ? 'text-white' : 'text-gray-700'}`}
                                    aria-label="Edit name"
                                >
                                    <Pencil size={13} strokeWidth={2} />
                                </button>
                            )}
                        </div>
                    )}
                    <p className={`text-base font-normal leading-relaxed mt-1 ${isDark ? 'text-white/80' : 'text-gray-700'}`}>
                        {greetingMessage.greeting}
                    </p>
                </div>
            </div>

            {/* Cards Scroll */}
            <div className="absolute left-0 right-0 z-30" style={{ top: '28vh', height: '52vh' }}>
                {todayConversations.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center px-4">
                        {/* Empty state placeholder if needed */}
                    </div>
                ) : (
                    <div
                        className="h-full overflow-x-auto overflow-y-hidden conversation-cards-scroll"
                        style={{
                            scrollSnapType: 'x mandatory',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                        }}
                    >
                        <style>{`
              .conversation-cards-scroll::-webkit-scrollbar {
                display: none;
              }
            `}</style>
                        <div className="flex flex-row gap-4 h-full px-4" style={{ width: 'max-content' }}>
                            {todayConversations.map((conv) => (
                                <div
                                    key={conv.id}
                                    className={`flex-shrink-0 rounded-2xl px-6 py-5 backdrop-blur-md border transition-all flex flex-col ${isDark
                                        ? 'bg-[rgba(255,255,255,0.08)] border-white/20'
                                        : 'bg-[rgba(255,255,255,0.5)] border-white/60'
                                        }`}
                                    style={{
                                        width: 'calc(100vw - 2rem)',
                                        maxWidth: '300px',
                                        maxHeight: '200px',
                                        scrollSnapAlign: 'start',
                                        scrollSnapStop: 'always'
                                    }}
                                >
                                    <div className="mb-1">
                                        <span className={`text-xs ${isDark ? 'text-white/50' : 'text-gray-600'}`}>
                                            {formatCardDate(conv.updatedAt || conv.createdAt)}
                                        </span>
                                    </div>
                                    <div className="mb-1.5">
                                        <h3 className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-gray-800'}`}>
                                            {conv.title || t('whisper.newConversation')}
                                        </h3>
                                    </div>
                                    <p className={`text-xs mb-3 line-clamp-2 leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-700'
                                        }`}>
                                        {getConversationSnippet(conv.messages)}
                                    </p>
                                    <button
                                        onClick={() => onContinue(conv.id)}
                                        className={`w-full py-2 px-4 rounded-xl text-xs font-medium transition-colors ${isDark
                                            ? 'bg-[rgba(255,255,255,0.15)] text-white hover:bg-white/20'
                                            : 'bg-[rgba(255,255,255,0.6)] text-gray-800 hover:bg-white/80'
                                            }`}
                                    >
                                        {t('whisper.continueConversation')}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
